import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, ChevronRight, RefreshCw, RotateCcw, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePOS } from "@/context/POSContext";
import * as printers from "@/lib/printerApi";

/**
 * Print jobs and printer alerts (settings sweep, 2026-10-19).
 *
 * The server keeps every print job and every printer failure, and can retry a
 * failed job — but nothing showed them: a KOT that died after its last attempt
 * told nobody (HANDOVER, "silent print failures").
 *
 * It sits above the printer setup as ONE summary line, collapsed (floor report
 * 2026-10-19): opened by default it listed every retry attempt of every job — forty
 * rows for one unplugged printer — and pushed the printers and routing, the actual
 * work on this page, off the bottom of the screen. Alerts are grouped per printer
 * with one "Mark all seen"; jobs keep their Retry.
 */

const errorText = failure => {
  const detail = failure?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return failure?.message || "Could not reach the server";
};

const FILTERS = [
  ["failed", "Failed"],
  ["waiting", "Waiting"],
  ["all", "All recent"],
];

const STATUS_LABEL = {
  pending: "Waiting to print",
  printing: "Printing",
  completed: "Printed",
  failed: "Failed",
  offline_queue: "Waiting — printer offline",
};

const TYPE_LABEL = { kot: "KOT", bill: "Bill", eod: "End of day", test: "Test" };

const JOBS_SHOWN = 10;
const JOB_LIMIT = 50;
const EVENT_LIMIT = 100;

const when = iso => {
  if (!iso) return "";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? String(iso) : at.toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
};

const plural = (n, one, many, atLeast = false) => `${n}${atLeast ? "+" : ""} ${n === 1 && !atLeast ? one : many}`;

export default function PrintJobsPanel() {
  const { token } = usePOS();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("failed");
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [state, setState] = useState({ loading: true, error: null, jobs: [], failedCount: 0, capped: {}, events: [], names: {} });
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const statuses = filter === "failed" ? ["failed"] : filter === "waiting" ? ["pending", "offline_queue"] : [null];
      const [jobLists, failed, events, list] = await Promise.all([
        Promise.all(statuses.map(status => printers.listPrintJobs(status, token))),
        filter === "failed" ? null : printers.listPrintJobs("failed", token),
        printers.listPrinterEvents(token, true),
        printers.listPrinters(token).catch(() => []),
      ]);
      const jobs = jobLists.flat().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      const names = Object.fromEntries((list || []).map(p => [p.id, p.name]));
      const failedJobs = failed || jobs.filter(job => job.status === "failed");
      // The lists are capped server-side (jobs 50, alerts 100); a full page means "at least".
      const failedCount = failedJobs.length;
      const capped = { failed: failedCount >= JOB_LIMIT, events: (events || []).length >= EVENT_LIMIT };
      setState({ loading: false, error: null, jobs, failedCount, capped, events: events || [], names });
    } catch (failure) {
      setState(s => ({ ...s, loading: false, error: errorText(failure) }));
    }
  }, [filter, token]);

  useEffect(() => { load(); }, [load]);

  const retry = async job => {
    setBusy(`job-${job.id}`);
    try {
      await printers.retryPrintJob(job.id, token);
      toast.success(`${TYPE_LABEL[job.print_type] || job.print_type} sent to the printer again`);
      await load();
    } catch (failure) {
      toast.error(`Could not retry: ${errorText(failure)}`);
    } finally {
      setBusy(null);
    }
  };

  const printerName = id => state.names[id] || (id ? `Printer ${id}` : "No printer");

  // One row per printer: how many alerts, and the newest one's words.
  const alertGroups = Object.values(
    state.events.reduce((groups, event) => {
      const key = event.printer_id ?? "none";
      const group = groups[key] || { printerId: event.printer_id ?? null, count: 0, latest: event, ids: [] };
      group.count += 1;
      group.ids.push(event.id);
      if (String(event.created_at) > String(group.latest.created_at)) group.latest = event;
      groups[key] = group;
      return groups;
    }, {})
  ).sort((a, b) => String(b.latest.created_at).localeCompare(String(a.latest.created_at)));

  const seenAll = async group => {
    setBusy(`alerts-${group.printerId}`);
    try {
      // No printer id means "every printer" to the bulk endpoint, so alerts with no
      // printer are acknowledged one by one — never by clearing everyone's.
      if (group.printerId == null) await Promise.all(group.ids.map(id => printers.markPrinterEventSeen(id, token)));
      else await printers.markPrinterEventsSeen(group.printerId, token);
      toast.success(`${printerName(group.printerId)}: alerts cleared`);
      await load();
    } catch (failure) {
      toast.error(`Could not mark them as seen: ${errorText(failure)}`);
    } finally {
      setBusy(null);
    }
  };

  const problems = state.failedCount + state.events.length;
  const summary = state.error
    ? `Could not load print jobs: ${state.error}`
    : state.loading && !problems
      ? "Checking…"
      : problems
        ? [
            state.failedCount ? plural(state.failedCount, "failed job", "failed jobs", state.capped?.failed) : null,
            state.events.length ? `${plural(state.events.length, "printer alert", "printer alerts", state.capped?.events)} from ${plural(alertGroups.length, "printer", "printers")}` : null,
          ].filter(Boolean).join(" · ")
        : "Nothing failed · no printer alerts";
  const Twisty = open ? ChevronDown : ChevronRight;
  const shownJobs = showAllJobs ? state.jobs : state.jobs.slice(0, JOBS_SHOWN);

  return (
    <section className="print-jobs" data-testid="print-jobs-panel">
      <div className="print-jobs-head">
        <button
          type="button"
          className="print-jobs-toggle"
          onClick={() => setOpen(next => !next)}
          aria-expanded={open}
          data-testid="print-jobs-toggle"
        >
          <Twisty className="w-4 h-4 shrink-0" aria-hidden="true" />
          {problems || state.error
            ? <TriangleAlert className="w-4 h-4 shrink-0 text-warning" aria-hidden="true" />
            : <CheckCircle2 className="w-4 h-4 shrink-0 text-success" aria-hidden="true" />}
          <span className="font-semibold">Print problems</span>
          <span className={problems || state.error ? "text-warning" : "text-muted-foreground"} data-testid="print-jobs-summary">{summary}</span>
        </button>
        <Button size="sm" variant="outline" className="h-8" onClick={load} disabled={state.loading} data-testid="print-jobs-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
        </Button>
      </div>

      {open && (
        <>
          {alertGroups.length > 0 && (
            <div className="print-jobs-alerts" data-testid="printer-alerts">
              {alertGroups.map(group => (
                <div key={group.printerId ?? "none"} className="print-jobs-alert" data-testid={`printer-alerts-${group.printerId}`}>
                  <TriangleAlert className="w-4 h-4 shrink-0" />
                  <span>
                    <strong>{printerName(group.printerId)}</strong> · {plural(group.count, "alert", "alerts")} · latest {when(group.latest.created_at)}:{" "}
                    {group.latest.message || group.latest.event_type}
                  </span>
                  <Button size="sm" variant="ghost" className="h-7" disabled={busy === `alerts-${group.printerId}`} onClick={() => seenAll(group)} data-testid={`printer-alerts-${group.printerId}-seen`}>
                    Mark all seen
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-1 border-b" role="tablist">
            {FILTERS.map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={filter === id}
                className={`px-3 py-1.5 text-meta font-semibold border-b-2 ${filter === id ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}
                onClick={() => { setFilter(id); setShowAllJobs(false); }} data-testid={`print-jobs-filter-${id}`}>
                {label}
              </button>
            ))}
          </div>

          {state.loading && !state.jobs.length && <p className="order-list-hint">Loading…</p>}
          {!state.loading && !state.error && !state.jobs.length && (
            <p className="order-list-hint" data-testid="print-jobs-empty">
              {filter === "failed" ? "No failed jobs." : filter === "waiting" ? "Nothing waiting to print." : "No recent jobs."}
            </p>
          )}
          {shownJobs.map(job => {
            const retryable = job.status === "failed" || job.status === "offline_queue";
            return (
              <div key={job.id} className={`print-jobs-row is-${job.status}`} data-testid={`print-job-${job.id}`}>
                <span className="print-jobs-what">
                  <strong>{TYPE_LABEL[job.print_type] || job.print_type}{job.kot_number ? ` #${job.kot_number}` : ""}</strong>
                  {job.order_id ? ` · order ${job.order_id}` : ""} · {printerName(job.printer_id)}
                </span>
                <span className="print-jobs-status">{STATUS_LABEL[job.status] || job.status} · tries {job.attempts}/{job.max_attempts}</span>
                {job.error_message && <span className="print-jobs-error" title={job.error_message}>{job.error_message}</span>}
                <span className="print-jobs-when">{when(job.created_at)}</span>
                {retryable && (
                  <Button variant="action" size="sm" className="h-7" disabled={busy === `job-${job.id}`} onClick={() => retry(job)} data-testid={`print-job-${job.id}-retry`}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />{busy === `job-${job.id}` ? "Retrying…" : "Retry"}
                  </Button>
                )}
              </div>
            );
          })}
          {!showAllJobs && state.jobs.length > JOBS_SHOWN && (
            <button type="button" className="order-list-hint underline text-left" onClick={() => setShowAllJobs(true)} data-testid="print-jobs-show-all">
              Show all {state.jobs.length} jobs
            </button>
          )}
        </>
      )}
    </section>
  );
}
